import { loadSearchResults } from './ods-search-results.js';

const historyKey = 'ods:recently:handles:v1';
const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(value);

class ODSSearch extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    const { signal } = this.events;
    this.input = this.querySelector('[data-search-input]');
    this.dialog = this.querySelector('dialog');
    this.initial = this.querySelector('[data-search-initial]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.cache = new Map();
    this.recentCards = new Map();
    this.revision = 0;
    this.recordCurrentProduct();
    this.input.addEventListener('input', () => this.change(), { signal });
    this.clearButton.addEventListener('click', () => {
      this.input.value = '';
      this.change();
      this.input.focus();
    }, { signal });
    this.input.form.addEventListener('submit', event => {
      if (!this.input.value.trim()) event.preventDefault();
    }, { signal });
    this.addEventListener('keydown', event => this.onKeydown(event), { signal });
    this.addEventListener('click', event => {
      const button = event.target.closest('[data-search-scroll]');
      if (!button) return;
      const track = button.closest('.ods-search__products').querySelector('[data-search-track]');
      track.scrollBy({ left: track.clientWidth * Number(button.dataset.searchScroll), behavior: 'auto' });
    }, { signal });
    this.addEventListener('scroll', () => this.updateScrollControls(), { capture: true, signal });
    this.dialog.addEventListener('close', () => this.cancel(), { signal });
    window.addEventListener('resize', () => this.syncViewport(), { signal });
    for (const event of ['resize', 'scroll']) {
      window.visualViewport?.addEventListener(event, () => this.syncViewport(), { signal });
    }
    this.dialogObserver?.disconnect();
    this.dialogObserver = new MutationObserver(() => {
      if (this.dialog.open) this.open();
      else this.cancel();
    });
    this.dialogObserver.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(() => this.updateScrollControls());
      this.resizeObserver.observe(this.dialog);
    }
    if (this.dialog.open) this.open();
  }

  disconnectedCallback() {
    this.cancel();
    this.events?.abort();
    this.dialogObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }

  open() {
    if (!this.dialog.open) return;
    this.syncViewport();
    this.change(true);
    this.updateScrollControls();
  }

  cancel() {
    clearTimeout(this.timer);
    this.revision++;
    this.request?.abort();
    this.recentRequest?.abort();
    this.recentPending = false;
    this.querySelector('[data-search-recent-region]')?.removeAttribute('aria-busy');
    this.removeAttribute('aria-busy');
  }

  syncViewport() {
    if (!this.dialog.open) return;
    const viewport = window.visualViewport;
    if (viewport && window.innerWidth < 1150) {
      this.dialog.style.setProperty('--ods-search-viewport-height', `${viewport.height}px`);
      this.dialog.style.setProperty('--ods-search-viewport-offset', `${viewport.offsetTop}px`);
    } else {
      this.dialog.style.removeProperty('--ods-search-viewport-height');
      this.dialog.style.removeProperty('--ods-search-viewport-offset');
    }
  }

  change(immediate = false) {
    clearTimeout(this.timer);
    this.request?.abort();
    const revision = ++this.revision;
    const term = this.input.value.trim();
    this.clearButton.hidden = !term;
    this.initial.hidden = Boolean(term);
    this.results.hidden = !term;
    this.results.replaceChildren();
    this.status.textContent = '';
    this.removeAttribute('aria-busy');
    if (!term) {
      this.dataset.state = 'initial';
      this.loadRecent();
      return;
    }
    this.dataset.state = 'loading';
    this.status.textContent = this.dataset.loading;
    this.setAttribute('aria-busy', 'true');
    if (immediate) this.search(term, revision);
    else this.timer = setTimeout(() => this.search(term, revision), 250);
  }

  searchURL(term, predictive = false) {
    const url = new URL(predictive ? this.dataset.predictiveUrl : this.dataset.searchUrl, location.origin);
    url.searchParams.set('q', term);
    if (predictive) {
      url.searchParams.set('resources[type]', 'query,product');
      url.searchParams.set('resources[limit]', '5');
      url.searchParams.set('resources[limit_scope]', 'each');
    } else url.searchParams.set('type', 'product');
    return url;
  }

  async search(term, revision) {
    const controller = new AbortController();
    this.request = controller;
    try {
      let markup = this.cache.get(term);
      if (!markup) {
        const url = this.searchURL(term, this.dataset.predictive === 'true');
        url.searchParams.set('section_id', 'ods-search-results');
        markup = await loadSearchResults(url.href, 'ods-search-results', controller.signal);
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(term, markup);
      }
      if (revision !== this.revision || controller.signal.aborted || !this.isConnected) return;
      this.results.innerHTML = markup;
      this.dataset.state = 'ready';
      const count = this.results.querySelector('[data-product-count]')?.dataset.productCount || '0';
      this.status.textContent = `${count} ${this.dataset.resultsLabel}`;
      this.updateScrollControls();
      document.dispatchEvent(new CustomEvent('currency:update'));
    } catch (error) {
      if (controller.signal.aborted || revision !== this.revision || !this.isConnected) return;
      this.dataset.state = 'error';
      this.status.textContent = this.dataset.error;
      const link = document.createElement('a');
      link.href = this.searchURL(term).href;
      link.className = 'ods-search__view-all';
      link.textContent = this.dataset.viewAll;
      this.results.replaceChildren(link);
    } finally {
      if (revision === this.revision) this.removeAttribute('aria-busy');
    }
  }

  readHistory() {
    try {
      const value = JSON.parse(localStorage.getItem(historyKey) || '[]');
      return Array.isArray(value) ? [...new Set(value.filter(validHandle))].slice(0, 12) : [];
    } catch { return []; }
  }

  recordCurrentProduct() {
    if (this.dataset.showRecent !== 'true' || !validHandle(this.dataset.currentHandle)) return;
    try {
      const handles = this.readHistory().filter(handle => handle !== this.dataset.currentHandle);
      localStorage.setItem(historyKey, JSON.stringify([this.dataset.currentHandle, ...handles].slice(0, 12)));
    } catch { /* Search remains usable when browser storage is unavailable. */ }
  }

  async loadRecent() {
    if (this.dataset.showRecent !== 'true' || !this.dialog.open || this.recentPending) return;
    const region = this.querySelector('[data-search-recent-region]');
    this.recentFallback ??= { markup: region.innerHTML, hidden: region.hidden };
    const limit = Math.min(8, Math.max(1, Number(this.dataset.recentLimit) || 6));
    const handles = this.readHistory().slice(0, limit);
    if (!handles.length) {
      this.restoreRecentFallback(region);
      return;
    }
    const controller = new AbortController();
    this.recentRequest = controller;
    this.recentPending = true;
    region.hidden = false;
    region.setAttribute('aria-busy', 'true');
    region.querySelector('[data-search-recent-heading]').textContent = this.dataset.recentHeading;
    const track = region.querySelector('[data-search-track]');
    track.setAttribute('aria-label', this.dataset.recentHeading);
    const slots = handles.map(handle => {
      const cached = this.recentCards.get(handle);
      if (cached) return new DOMParser().parseFromString(cached, 'text/html').querySelector('.ods-search__card');
      const placeholder = document.createElement('div');
      placeholder.className = 'ods-search__card ods-search__placeholder';
      placeholder.setAttribute('data-search-recent-placeholder', '');
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.innerHTML = '<div class="ods-search__image"></div><div class="ods-search__card-info"><span></span><span></span></div>';
      return placeholder;
    });
    track.replaceChildren(...slots);
    if (!this.input.value.trim() && handles.some(handle => !this.recentCards.has(handle))) this.status.textContent = this.dataset.loading;
    this.updateScrollControls();
    try {
      await Promise.all(handles.map(async (handle, index) => {
        if (this.recentCards.has(handle)) return;
        let card;
        try {
          const root = this.dataset.rootUrl.replace(/\/?$/, '/');
          const url = new URL(`${root}products/${encodeURIComponent(handle)}`, location.origin);
          url.searchParams.set('section_id', 'ods-search-recent-product');
          const markup = await loadSearchResults(url.href, 'ods-search-recent-product', controller.signal);
          card = new DOMParser().parseFromString(markup, 'text/html').querySelector('.ods-search__card');
        } catch { /* Keep other recent products usable when one request fails. */ }
        if (controller.signal.aborted || !this.isConnected) return;
        if (card) {
          this.recentCards.set(handle, card.outerHTML);
          slots[index].replaceWith(card);
        } else slots[index].remove();
        this.updateScrollControls();
      }));
      if (controller.signal.aborted || !this.isConnected) return;
      if (!track.querySelector('.ods-search__card')) this.restoreRecentFallback(region);
      this.updateScrollControls();
      document.dispatchEvent(new CustomEvent('currency:update'));
    } finally {
      if (this.recentRequest === controller) {
        this.recentPending = false;
        region.removeAttribute('aria-busy');
        if (!this.input.value.trim()) this.status.textContent = '';
      }
    }
  }

  restoreRecentFallback(region) {
    region.innerHTML = this.recentFallback.markup;
    region.hidden = this.recentFallback.hidden;
    this.updateScrollControls();
  }

  updateScrollControls() {
    this.querySelectorAll('[data-search-track]').forEach(track => {
      const controls = track.closest('.ods-search__products').querySelector('[data-search-scroll-controls]');
      if (!controls) return;
      const overflow = track.scrollWidth - track.clientWidth;
      controls.hidden = overflow < 2;
      controls.querySelector('[data-search-scroll="-1"]').disabled = Math.abs(track.scrollLeft) < 2;
      controls.querySelector('[data-search-scroll="1"]').disabled = Math.abs(track.scrollLeft) >= overflow - 2;
    });
  }

  onKeydown(event) {
    const links = [...(this.input.value.trim() ? this.results : this.initial).querySelectorAll('a[href]')]
      .filter(link => !link.closest('[hidden]'));
    if (!links.length) return;
    const index = links.indexOf(document.activeElement);
    if (event.key === 'ArrowDown' && (event.target === this.input || index >= 0)) {
      event.preventDefault();
      links[Math.min(index + 1, links.length - 1)].focus();
    } else if (event.key === 'ArrowUp' && index >= 0) {
      event.preventDefault();
      if (index === 0) this.input.focus();
      else links[index - 1].focus();
    }
  }
}

if (!customElements.get('ods-search')) customElements.define('ods-search', ODSSearch);
